import styled from '@emotion/styled';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import ErrorMessage from '~/common/componentMapper/PdfViewer/ErrorMessage';
import Toolbar from '~/common/componentMapper/PdfViewer/Toolbar';
import type {
  Citation,
  DocumentOption,
} from '~/common/componentMapper/PdfViewer/types';
import Box from '~/common/components/Box';
import { TOP_NAV_HEIGHT } from '~/common/components/GlobalLayout/PageLayout/Styled';
import Oops from '~/common/components/Oops';
import Spinner from '~/common/components/Spinner';
import { logError, logWarning } from '~/common/util/logger';
import {
  createHighlightedPdfBlob,
  getPdfPageCount,
} from '~/common/util/pdfUtils';
import { useDocumentPdfQuery } from '~/pages/ClassifyDocumentsV2/hooks/queries';
import ErrorBoundary from '~/pages/Home/ErrorBoundary/ErrorBoundary';
import { useSelectedAlert } from '~/stores/navigationStore';
import palette from '~/theme/mui/palette';

import {
  MAX_ZOOM,
  MIN_ZOOM,
  buildIframeSrc,
  clampScale,
  toValidPageNumber,
} from './pdfViewerWithIframeHelper';
import { documentTypesToResetCitation } from './pdfViewerWithIframeTypes';

const StyledContainer = styled<any>(Box)`
  width: 100%;
  height: calc(100vh - ${TOP_NAV_HEIGHT} - var(--gl-header-height, 0px));
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid ${(props) => props.theme.colors?.gray[200]};
  background-color: ${palette.black};
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
`;

type PDFViewerWithIframeProps = {
  defaultDocumentId?: number | string;
  defaultPage?: number | string;
  documentOptions?: Array<DocumentOption>;
};

function PDFViewerWithIframe({
  defaultDocumentId,
  defaultPage,
  documentOptions = [],
}: Readonly<PDFViewerWithIframeProps>) {
  const selectedAlert = useSelectedAlert();
  const documentTypeId = String(
    selectedAlert?.additionalReferences?.DocumentTypeId ?? '',
  );

  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [activeCitation, setActiveCitation] = useState<Citation>(null);
  const [currentDocId, setCurrentDocId] = useState<number>(
    defaultDocumentId
      ? Number(defaultDocumentId)
      : Number(documentOptions[0]?.id ?? 0),
  );
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.1);
  const isMountedRef = useRef(true);

  const {
    data: pdfBlob,
    isLoading,
    isFetching,
    isError,
  } = useDocumentPdfQuery(currentDocId || undefined);

  useEffect(() => {
    if (documentTypesToResetCitation.has(documentTypeId)) {
      setActiveCitation(null);
    }
  }, [documentTypeId]);

  useEffect(() => {
    if (!pdfBlob) {
      setObjectUrl(null);
      return;
    }

    let cancelled = false;
    let createdUrl: string | null = null;

    const buildDisplayUrl = async () => {
      const boundingBoxes = activeCitation?.boundingBoxes ?? [];
      let blob: Blob = pdfBlob;

      if (boundingBoxes.length > 0) {
        try {
          const bytes = await pdfBlob.arrayBuffer();
          blob = await createHighlightedPdfBlob(bytes, boundingBoxes, {
            filled: activeCitation?.showHighlight,
          });
        } catch (error) {
          logError('Error rendering PDF highlights:', error);
          blob = pdfBlob;
        }
      }

      if (cancelled) return;
      createdUrl = URL.createObjectURL(blob);
      setObjectUrl(createdUrl);
    };

    buildDisplayUrl();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [pdfBlob, activeCitation]);

  useEffect(() => {
    if (!pdfBlob) {
      setPageCount(0);
      return;
    }

    let cancelled = false;
    pdfBlob
      .arrayBuffer()
      .then(getPdfPageCount)
      .then((count) => {
        if (!cancelled) setPageCount(count);
      })
      .catch((error) => logError('Error reading PDF page count:', error));

    return () => {
      cancelled = true;
    };
  }, [pdfBlob]);

  const handlePDFCitationMessage = useCallback((event: MessageEvent) => {
    try {
      if (
        !(
          (event.data?.type === 'pdfCitation' && event.data.payload?.length) ||
          event.data?.payload?.boundingBoxes?.length
        )
      )
        return;

      const citation = event.data.payload[0] || event.data.payload;
      if (!isMountedRef.current) {
        logWarning('Attempted to set active citation on unmounted component');
        return;
      }
      if (citation?.documentId && citation?.boundingBoxes?.length !== 0) {
        setCurrentDocId(citation.documentId);
      }
      setActiveCitation(citation?.boundingBoxes?.length ? citation : null);
    } catch (error) {
      logError('Error handling PDF citation message:', error);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    window.addEventListener('message', handlePDFCitationMessage);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('message', handlePDFCitationMessage);
    };
  }, [handlePDFCitationMessage]);

  const citationPageNumber = activeCitation?.boundingBoxes?.length
    ? activeCitation.boundingBoxes[0]?.pageNumber
    : undefined;
  const activePage = citationPageNumber ?? toValidPageNumber(defaultPage);

  useEffect(() => {
    setCurrentPage(activePage ?? 1);
  }, [activePage, currentDocId]);

  const handlePageChange = (page: number) => {
    const maxPage = pageCount || page;
    setCurrentPage(Math.min(Math.max(page, 1), maxPage));
  };

  const handleScaleChange = (nextScale: number) => {
    setScale(clampScale(nextScale));
  };

  const isReady = !!objectUrl && !isFetching && !isError;
  const hasNoDocument = currentDocId <= 0 && !isFetching && !isError;

  return (
    <ErrorBoundary selectedAlert={selectedAlert} fallback={<Oops />}>
      {isError && <Oops />}
      {isFetching && isLoading && <Spinner />}
      {hasNoDocument && (
        <StyledContainer
          as="article"
          sx={{
            flexDirection: 'column',
          }}
        >
          <Toolbar
            documentOptions={documentOptions}
            documentId={currentDocId}
            currentPageNumber={0}
            totalPages={0}
            setPageNumberChange={handlePageChange}
            scale={scale}
            zoom={Math.round(scale * 100)}
            minZoom={MIN_ZOOM * 100}
            maxZoom={MAX_ZOOM * 100}
            onScaleChange={handleScaleChange}
          />
          <Box sx={{ width: '100%', height: '100%' }}>
            <ErrorMessage message="No Document(s) Available" />
          </Box>
        </StyledContainer>
      )}
      {isReady && (
        <StyledContainer
          as="article"
          sx={{
            flexDirection: 'column',
          }}
        >
          <Toolbar
            documentOptions={documentOptions}
            documentId={currentDocId}
            currentPageNumber={currentPage}
            totalPages={pageCount}
            setPageNumberChange={handlePageChange}
            scale={scale}
            zoom={Math.round(scale * 100)}
            minZoom={MIN_ZOOM * 100}
            maxZoom={MAX_ZOOM * 100}
            onScaleChange={handleScaleChange}
          />
          <StyledIframe
            key={`${objectUrl}-${currentPage}-${scale}`}
            allow="fullscreen"
            title={selectedAlert?.ruleReference?.id?.toString()}
            src={
              buildIframeSrc(objectUrl, currentPage, Math.round(scale * 100)) +
              '&toolbar=0'
            }
            id="documentViewer"
          />
        </StyledContainer>
      )}
    </ErrorBoundary>
  );
}

const arePropsEqual = (
  prevProps: Readonly<PDFViewerWithIframeProps>,
  nextProps: Readonly<PDFViewerWithIframeProps>,
) =>
  prevProps.defaultDocumentId === nextProps.defaultDocumentId &&
  prevProps.defaultPage === nextProps.defaultPage &&
  prevProps.documentOptions?.length === nextProps.documentOptions?.length;

const MemoizedPDFViewerWithIframe = memo(PDFViewerWithIframe, arePropsEqual);

export default MemoizedPDFViewerWithIframe;
